import { useMemo, useCallback } from 'react';
import { FilterStateModel, DEFAULT_FILTER_STATE } from 'models/Enterprise/FieldsManagerModel';
import { FieldFilter } from 'models/Enterprise/FieldsManagerModel';

/**
 * Computes the visibility/disabled state for a single filter entry.
 *
 * Business Rules:
 * - FieldSelect: Always visible and enabled (user must always be able to pick a field)
 * - Operator: Visible and enabled ONLY after a field has been selected
 * - Values: Visible and enabled ONLY after a field has been selected
 * - RemoveButton: Always visible and enabled
 *
 * Future extensibility:
 * - Operator visibility/options could vary by field TypeName
 * - Values could be disabled for certain operator types (e.g. "Is empty" needs no values)
 * - FieldSelect could be disabled in read-only/edit modes
 */
const computeFilterState = (filter: FieldFilter): FilterStateModel => {
  const hasFieldSelected = !!filter.filterGuid;

  return {
    ...DEFAULT_FILTER_STATE,
    Operator: {
      visible: hasFieldSelected,
      disabled: !hasFieldSelected,
    },
    Values: {
      visible: hasFieldSelected,
      disabled: !hasFieldSelected,
    },
  };
};

/**
 * Custom hook to memoize filter state computation function.
 * Returns a stable reference to prevent unnecessary recalculations.
 *
 * @returns Memoized computeFilterState function
 */
const useComputeFilterState = () => {
  return useCallback((filter: FieldFilter): FilterStateModel => {
    return computeFilterState(filter);
  }, []);
};

/**
 * Custom hook to compute filter states for all filters in a field's filter list.
 * Memoized to prevent unnecessary recalculations.
 *
 * Mirrors `useFieldStates` — returns a Map<filterIndex, FilterStateModel> so the
 * consuming component can look up each filter's state by position.
 *
 * Business Rules (current):
 * - Operator and Values are hidden until a field/question is selected
 * - RemoveButton is always available
 *
 * Future Business Rules (prepared but not enforced):
 * - Operator options may change based on field TypeName
 * - Values may be disabled for operators that don't require them (e.g. "Is empty", "Is not empty")
 * - FieldSelect may become disabled in certain edit modes
 * - Entire filter rows may become read-only based on permissions
 *
 * @param filters - Array of filter entries for a field
 * @returns Map of filter index to computed FilterStateModel
 */
export const useFilterStates = (filters: FieldFilter[]): Map<number, FilterStateModel> => {
  const computeState = useComputeFilterState();

  return useMemo(() => {
    const stateMap = new Map<number, FilterStateModel>();
    filters.forEach((filter, index) => {
      stateMap.set(index, computeState(filter));
    });
    return stateMap;
  }, [filters, computeState]);
};
